import { z } from 'zod';
import { WallClockSchema } from './attendance';
import { FileSummarySchema, PersonRefSchema, SiteRefSchema } from './property';

/**
 * Safety (docs/specs/safety.md, D68): the accident book with near misses, corrective actions
 * and RIDDOR deadlines; lone working mirrored from the old system with Aroo's own alarm
 * escalation; dash-camera compliance from the daily van checks. Dates are YYYY-MM-DD, instants
 * ISO, and times read from the old system (or when an accident happened) are London wall clock
 * "YYYY-MM-DDTHH:MM:SS" with no offset.
 */

const IsoDate = z.iso.date();
const Instant = z.iso.datetime();
/** The report form's "when": wall clock with or without seconds. */
export const WallClockInputSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/, 'Use YYYY-MM-DDTHH:MM');
const Wall = WallClockSchema;

export const SAFETY_PERMISSIONS = {
  report: 'safety.incidents.report',
  view: 'safety.scope.view',
  loneWorking: 'safety.loneworking.view',
  admin: 'safety.admin.manage',
} as const;

// Vocabularies ---------------------------------------------------------------------

export const IncidentKindSchema = z.enum(['accident', 'near_miss']);
export type IncidentKind = z.infer<typeof IncidentKindSchema>;
export const INCIDENT_KIND_LABELS: Record<IncidentKind, string> = {
  accident: 'Accident',
  near_miss: 'Near miss',
};

export const SafetyIncidentStatusSchema = z.enum([
  'reported',
  'investigating',
  'actions_agreed',
  'closed',
]);
export type SafetyIncidentStatus = z.infer<typeof SafetyIncidentStatusSchema>;
export const INCIDENT_STATUS_LABELS: Record<SafetyIncidentStatus, string> = {
  reported: 'Reported',
  investigating: 'Under investigation',
  actions_agreed: 'Actions agreed',
  closed: 'Closed',
};

export const IncidentPersonKindSchema = z.enum(['staff', 'visitor', 'contractor', 'none']);
export type IncidentPersonKind = z.infer<typeof IncidentPersonKindSchema>;
export const PERSON_KIND_LABELS: Record<IncidentPersonKind, string> = {
  staff: 'Member of staff',
  visitor: 'Visitor or customer',
  contractor: 'Contractor',
  none: 'Nobody (near miss / property only)',
};

export const HospitalSchema = z.enum(['none', 'a_and_e', 'admitted']);
export type Hospital = z.infer<typeof HospitalSchema>;
export const HOSPITAL_LABELS: Record<Hospital, string> = {
  none: 'No hospital',
  a_and_e: 'Went to A&E',
  admitted: 'Admitted to hospital',
};

export const RiddorDecisionSchema = z.enum(['undecided', 'reportable', 'not_reportable']);
export type RiddorDecision = z.infer<typeof RiddorDecisionSchema>;

export const RiddorCategorySchema = z.enum([
  'death',
  'specified_injury',
  'over_7_day',
  'occupational_disease',
  'dangerous_occurrence',
]);
export type RiddorCategory = z.infer<typeof RiddorCategorySchema>;
export const RIDDOR_CATEGORY_LABELS: Record<RiddorCategory, string> = {
  death: 'Death',
  specified_injury: 'Specified injury',
  over_7_day: 'Over-7-day incapacitation',
  occupational_disease: 'Occupational disease',
  dangerous_occurrence: 'Dangerous occurrence',
};

export const RiddorStatusSchema = z.enum([
  'undecided',
  'not_reportable',
  'due',
  'due_soon',
  'overdue',
  'reported',
]);
export type RiddorStatus = z.infer<typeof RiddorStatusSchema>;

export const ActionStatusSchema = z.enum(['open', 'done', 'cancelled']);
export type SafetyActionStatus = z.infer<typeof ActionStatusSchema>;

// Incidents --------------------------------------------------------------------------

export const RiddorSchema = z.object({
  decision: RiddorDecisionSchema,
  category: RiddorCategorySchema.nullable(),
  reason: z.string().nullable(),
  diagnosedOn: IsoDate.nullable(),
  deadline: IsoDate.nullable(),
  /** death, specified injury, dangerous occurrence, disease: notify HSE without delay. */
  withoutDelay: z.boolean(),
  /** Calendar days to the deadline (negative = overdue); null when nothing is due. */
  daysLeft: z.number().int().nullable(),
  status: RiddorStatusSchema,
  decidedBy: PersonRefSchema.nullable(),
  decidedAt: Instant.nullable(),
  reportedOn: IsoDate.nullable(),
  hseRef: z.string().nullable(),
  reportedBy: PersonRefSchema.nullable(),
});
export type Riddor = z.infer<typeof RiddorSchema>;

export const IncidentSummarySchema = z.object({
  id: z.uuid(),
  ref: z.string(),
  kind: IncidentKindSchema,
  site: SiteRefSchema,
  occurredAt: Wall,
  status: SafetyIncidentStatusSchema,
  personKind: IncidentPersonKindSchema,
  /** The person involved: a staff name or the free-text visitor/contractor name. */
  personName: z.string().nullable(),
  summary: z.string(),
  injured: z.boolean(),
  riddor: RiddorSchema.pick({ decision: true, status: true, deadline: true, daysLeft: true }),
  openActions: z.number().int(),
  reportedAt: Instant,
});
export type IncidentSummary = z.infer<typeof IncidentSummarySchema>;

export const IncidentsResponseSchema = z.object({ incidents: z.array(IncidentSummarySchema) });
export type IncidentsResponse = z.infer<typeof IncidentsResponseSchema>;

/** Confidential (investigators + the site's BM only); absent from the case for anyone else. */
export const InjurySchema = z.object({
  bodyPart: z.string().nullable(),
  injuryType: z.string().nullable(),
  firstAidGiven: z.boolean().nullable(),
  firstAidDetail: z.string().nullable(),
  hospital: HospitalSchema.nullable(),
  notes: z.string().nullable(),
});
export type Injury = z.infer<typeof InjurySchema>;

export const SafetyActionSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  owner: PersonRefSchema,
  dueOn: IsoDate,
  status: ActionStatusSchema,
  overdue: z.boolean(),
  doneBy: PersonRefSchema.nullable(),
  doneAt: Instant.nullable(),
  note: z.string().nullable(),
});
export type SafetyAction = z.infer<typeof SafetyActionSchema>;

export const IncidentEventSchema = z.object({
  id: z.uuid(),
  at: Instant,
  actor: PersonRefSchema.nullable(),
  kind: z.string(),
  fromStatus: z.string().nullable(),
  toStatus: z.string().nullable(),
  note: z.string().nullable(),
});
export type IncidentEvent = z.infer<typeof IncidentEventSchema>;

export const LinkedAbsenceSchema = z.object({
  id: z.uuid(),
  typeName: z.string(),
  startDate: IsoDate,
  endDate: IsoDate.nullable(),
  status: z.string(),
  note: z.string().nullable(),
  /** True when recorded against this case; false when Aroo found a matching one. */
  linked: z.boolean(),
});
export type LinkedAbsence = z.infer<typeof LinkedAbsenceSchema>;

export const IncidentDetailSchema = IncidentSummarySchema.extend({
  location: z.string().nullable(),
  person: PersonRefSchema.nullable(),
  description: z.string(),
  immediateActions: z.string().nullable(),
  witnesses: z.string().nullable(),
  /** Present only for investigators and the site's BM. */
  injury: InjurySchema.optional(),
  /** Present only for investigators and the site's BM (it is health data). */
  absence: LinkedAbsenceSchema.nullable().optional(),
  investigator: PersonRefSchema.nullable(),
  investigationNotes: z.string().nullable(),
  riddorDetail: RiddorSchema,
  actions: z.array(SafetyActionSchema),
  events: z.array(IncidentEventSchema),
  photos: z.array(FileSummarySchema),
  reportedBy: PersonRefSchema,
  closedBy: PersonRefSchema.nullable(),
  closedAt: Instant.nullable(),
  closureNote: z.string().nullable(),
  can: z.object({
    investigate: z.boolean(),
    seeConfidential: z.boolean(),
    addPhotos: z.boolean(),
    /** May record an "Accident at work" absence for the injured person (2b record path). */
    recordAbsence: z.boolean(),
  }),
});
export type IncidentDetail = z.infer<typeof IncidentDetailSchema>;

const Text = (max: number) => z.string().trim().max(max);
const OptText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .optional()
    .transform((v) => (v ? v : null));

export const IncidentPatchSchema = z.object({
  investigationNotes: OptText(10000),
  location: OptText(200),
  witnesses: OptText(2000),
  immediateActions: OptText(5000),
});
export type IncidentPatch = z.input<typeof IncidentPatchSchema>;

export const RiddorDecisionWriteSchema = z.discriminatedUnion('decision', [
  z.object({
    decision: z.literal('reportable'),
    category: RiddorCategorySchema,
    reason: Text(1000).min(3),
    /** Occupational disease: the date of the written diagnosis (the deadline counts from it). */
    diagnosedOn: IsoDate.nullable().optional(),
  }),
  z.object({ decision: z.literal('not_reportable'), reason: Text(1000).min(3) }),
]);
export type RiddorDecisionWrite = z.infer<typeof RiddorDecisionWriteSchema>;

export const RiddorReportedSchema = z.object({
  reportedOn: IsoDate,
  hseRef: Text(50).min(2),
});
export type RiddorReported = z.infer<typeof RiddorReportedSchema>;

export const SafetyActionWriteSchema = z.object({
  title: Text(300).min(3),
  ownerId: z.uuid(),
  dueOn: IsoDate,
});
export type SafetyActionWrite = z.infer<typeof SafetyActionWriteSchema>;

export const SafetyActionStepSchema = z.object({
  action: z.enum(['done', 'cancel', 'reopen']),
  note: OptText(1000),
});
export type SafetyActionStep = z.input<typeof SafetyActionStepSchema>;

export const IncidentStepSchema = z.object({ note: OptText(2000) });
export type IncidentStep = z.input<typeof IncidentStepSchema>;

export const SafetyOkSchema = z.object({ ok: z.literal(true) });
export const SafetyIdSchema = z.object({ id: z.uuid() });
export const IncidentReportedSchema = z.object({ id: z.uuid(), ref: z.string() });
export const PhotosAddedSchema = z.object({ added: z.number().int() });

export const SafetyPeopleSearchResponseSchema = z.object({
  people: z.array(PersonRefSchema.extend({ site: z.string().nullable() })),
});
export type SafetyPeopleSearchResponse = z.infer<typeof SafetyPeopleSearchResponseSchema>;

// Lone working -----------------------------------------------------------------------

export const LoneSessionStatusSchema = z.enum(['ACTIVE', 'OVERDUE', 'ENDED', 'AUTO_CLOSED']);
export const LoneResolutionSchema = z.enum([
  'WORKER_CHECKED_IN',
  'CONFIRMED_SAFE',
  'FALSE_ALARM',
  'EMERGENCY_SERVICES',
  'OTHER',
]);
export type LoneResolution = z.infer<typeof LoneResolutionSchema>;
export const LONE_RESOLUTION_LABELS: Record<LoneResolution, string> = {
  WORKER_CHECKED_IN: 'Worker checked in',
  CONFIRMED_SAFE: 'Confirmed safe (spoke to them)',
  FALSE_ALARM: 'False alarm',
  EMERGENCY_SERVICES: 'Emergency services called',
  OTHER: 'Other',
};

export const LoneSessionSchema = z.object({
  sessionId: z.string(),
  person: PersonRefSchema.nullable(),
  site: SiteRefSchema.nullable(),
  branch: z.string(),
  status: LoneSessionStatusSchema,
  startedAt: Wall,
  nextDueAt: Wall,
  lastCheckInAt: Wall.nullable(),
  checkIns: z.number().int(),
  /** Past the deadline plus grace now (London wall clock). */
  overdue: z.boolean(),
  /** Minutes past the deadline (negative = minutes left). */
  minutesPastDue: z.number().int(),
  endedAt: Wall.nullable(),
  endReason: z.string().nullable(),
  alerts: z.number().int(),
});
export type LoneSession = z.infer<typeof LoneSessionSchema>;

export const AlarmStateSchema = z.enum(['open', 'acknowledged', 'resolved', 'cleared']);
export type AlarmState = z.infer<typeof AlarmStateSchema>;
export const ALARM_LEVEL_LABELS: Record<number, string> = {
  0: 'Not yet sent',
  1: 'Branch manager',
  2: 'Regional manager',
  3: 'Operations director',
};

export const LoneAlarmSchema = z.object({
  id: z.uuid(),
  kind: z.enum(['missed_check_in', 'panic']),
  sessionId: z.string(),
  person: PersonRefSchema.nullable(),
  site: SiteRefSchema.nullable(),
  dueAt: Wall.nullable(),
  openedAt: Instant,
  level: z.number().int(),
  levelAt: Instant.nullable(),
  /** When the next level is told if nobody acknowledges (null when it won't escalate). */
  nextLevelAt: Instant.nullable(),
  state: AlarmStateSchema,
  told: z.array(z.object({ level: z.number().int(), person: PersonRefSchema, at: Instant })),
  acknowledgedBy: PersonRefSchema.nullable(),
  acknowledgedAt: Instant.nullable(),
  ackNote: z.string().nullable(),
  resolvedBy: PersonRefSchema.nullable(),
  resolvedAt: Instant.nullable(),
  resolution: LoneResolutionSchema.nullable(),
  resolutionNote: z.string().nullable(),
  clearedAt: Instant.nullable(),
  clearedReason: z.string().nullable(),
  /** Minutes from opening to acknowledgement (history). */
  minutesToAck: z.number().int().nullable(),
  oldSystemAlerts: z.number().int(),
});
export type LoneAlarm = z.infer<typeof LoneAlarmSchema>;

export const LegacyLonePolicySchema = z.object({
  enabled: z.boolean(),
  checkInIntervalMinutes: z.number().int(),
  graceMinutes: z.number().int(),
  escalationRepeatMinutes: z.number().int(),
  maxDurationMinutes: z.number().int(),
  excludedBranches: z.string(),
});
export type LegacyLonePolicy = z.infer<typeof LegacyLonePolicySchema>;

export const SyncFreshnessSchema = z.object({
  lastOkAt: Instant.nullable(),
  stale: z.boolean(),
  failing: z.array(z.string()),
});
export type SyncFreshness = z.infer<typeof SyncFreshnessSchema>;

export const LoneBoardSchema = z.object({
  now: z.array(LoneSessionSchema),
  alarms: z.array(LoneAlarmSchema),
  policy: LegacyLonePolicySchema.nullable(),
  escalation: z.object({
    rmAfterMinutes: z.number().int(),
    directorAfterMinutes: z.number().int(),
  }),
  sync: SyncFreshnessSchema,
  bridgeMode: z.enum(['off', 'dry_run', 'on']),
});
export type LoneBoard = z.infer<typeof LoneBoardSchema>;

export const LoneHistorySchema = z.object({
  sessions: z.array(LoneSessionSchema),
  alarms: z.array(LoneAlarmSchema),
  from: IsoDate,
  to: IsoDate,
});
export type LoneHistory = z.infer<typeof LoneHistorySchema>;

export const AlarmAcknowledgeSchema = z.object({ note: OptText(500) });
export type AlarmAcknowledge = z.input<typeof AlarmAcknowledgeSchema>;
export const AlarmResolveSchema = z.object({
  resolution: LoneResolutionSchema,
  note: OptText(500),
});
export type AlarmResolve = z.input<typeof AlarmResolveSchema>;
export const AlarmResponseSchema = z.object({ alarm: LoneAlarmSchema });

// Camera compliance ----------------------------------------------------------------------

export const CameraFlagSchema = z.enum([
  'internal_no',
  'external_no',
  'internal_unanswered',
  'external_unanswered',
  'internal_photo_missing',
  'external_photo_missing',
]);
export type CameraFlag = z.infer<typeof CameraFlagSchema>;
export const CAMERA_FLAG_LABELS: Record<CameraFlag, string> = {
  internal_no: 'Internal camera: No',
  external_no: 'External camera: No',
  internal_unanswered: 'Internal camera: not answered',
  external_unanswered: 'External camera: not answered',
  internal_photo_missing: 'Internal camera photo missing',
  external_photo_missing: 'External camera photo missing',
};

export const CameraReviewOutcomeSchema = z.enum([
  'camera_fixed',
  'photo_not_needed',
  'spoke_to_driver',
  'other',
]);
export type CameraReviewOutcome = z.infer<typeof CameraReviewOutcomeSchema>;
export const CAMERA_REVIEW_LABELS: Record<CameraReviewOutcome, string> = {
  camera_fixed: 'Camera fixed',
  photo_not_needed: 'Photo not needed this time',
  spoke_to_driver: 'Spoke to the driver',
  other: 'Other',
};

export const CameraCheckSchema = z.object({
  id: z.number().int(),
  reg: z.string().nullable(),
  site: SiteRefSchema.nullable(),
  branch: z.string().nullable(),
  driver: PersonRefSchema.nullable(),
  completedAt: Wall,
  flags: z.array(CameraFlagSchema),
  /** Links to the photo on the old system (null: missing, or links switched off). */
  internalPhotoUrl: z.string().nullable(),
  externalPhotoUrl: z.string().nullable(),
  /** True when a photo exists on the old system but Aroo does not link to it. */
  photoOnOldSystem: z.boolean(),
  alerted: z.boolean(),
  review: z
    .object({
      by: PersonRefSchema,
      at: Instant,
      outcome: CameraReviewOutcomeSchema,
      note: z.string().nullable(),
    })
    .nullable(),
});
export type CameraCheck = z.infer<typeof CameraCheckSchema>;

export const CameraBranchSchema = z.object({
  site: SiteRefSchema.nullable(),
  branch: z.string(),
  checks7: z.number().int(),
  passing7: z.number().int(),
  pct7: z.number().nullable(),
  checks30: z.number().int(),
  passing30: z.number().int(),
  pct30: z.number().nullable(),
  failingUnreviewed7: z.number().int(),
});
export type CameraBranch = z.infer<typeof CameraBranchSchema>;

export const CameraComplianceSchema = z.object({
  branches: z.array(CameraBranchSchema),
  totals: CameraBranchSchema.omit({ site: true, branch: true }),
  failing: z.array(CameraCheckSchema),
  canReview: z.boolean(),
  sync: SyncFreshnessSchema,
});
export type CameraCompliance = z.infer<typeof CameraComplianceSchema>;

export const CameraReviewWriteSchema = z.object({
  checkIds: z.array(z.number().int().positive()).min(1).max(500),
  outcome: CameraReviewOutcomeSchema,
  note: OptText(500),
});
export type CameraReviewWrite = z.input<typeof CameraReviewWriteSchema>;
export const CameraReviewResponseSchema = z.object({ reviewed: z.number().int() });

// Home and settings ----------------------------------------------------------------------

export const SafetyHomeSchema = z.object({
  can: z.object({
    report: z.boolean(),
    view: z.boolean(),
    investigate: z.boolean(),
    loneWorking: z.boolean(),
    cameras: z.boolean(),
  }),
  /** Sites the viewer may report at (the report form's choices). */
  reportSites: z.array(SiteRefSchema),
  counts: z.object({
    openIncidents: z.number().int(),
    openAccidents: z.number().int(),
    openNearMisses: z.number().int(),
    toInvestigate: z.number().int(),
    riddorOutstanding: z.number().int(),
    riddorOverdue: z.number().int(),
    loneWorkersNow: z.number().int(),
    overdueCheckIns: z.number().int(),
    openAlarms: z.number().int(),
  }),
  openIncidents: z.array(IncidentSummarySchema),
  riddorDeadlines: z.array(IncidentSummarySchema),
  loneNow: z.array(LoneSessionSchema),
  alarms: z.array(LoneAlarmSchema),
  camera: CameraBranchSchema.omit({ site: true, branch: true }).nullable(),
});
export type SafetyHome = z.infer<typeof SafetyHomeSchema>;

export const SafetySettingsSchema = z.object({
  escalationPerson: PersonRefSchema.nullable(),
  /** Who the Ops director step reaches today, and how it was resolved. */
  opsDirector: z.object({
    people: z.array(PersonRefSchema),
    via: z.enum(['setting', 'ops_director_seat', 'director_role', 'none']),
  }),
  loneWorkingEnabled: z.boolean(),
  rmAfterMinutes: z.number().int(),
  directorAfterMinutes: z.number().int(),
  riddorReminderDays: z.array(z.number().int()),
  cameraAlertsEnabled: z.boolean(),
  cameraAlertEmail: z.boolean(),
  cameraPhotoLinks: z.boolean(),
  cameraAlertsFrom: Instant,
  legacyPolicy: LegacyLonePolicySchema.nullable(),
  bridgeMode: z.enum(['off', 'dry_run', 'on']),
});
export type SafetySettings = z.infer<typeof SafetySettingsSchema>;

export const SafetySettingsPatchSchema = z.object({
  escalationPersonId: z.uuid().nullable().optional(),
  loneWorkingEnabled: z.boolean().optional(),
  rmAfterMinutes: z.number().int().min(1).max(240).optional(),
  directorAfterMinutes: z.number().int().min(1).max(240).optional(),
  riddorReminderDays: z.array(z.number().int().min(0).max(60)).max(6).optional(),
  cameraAlertsEnabled: z.boolean().optional(),
  cameraAlertEmail: z.boolean().optional(),
  cameraPhotoLinks: z.boolean().optional(),
});
export type SafetySettingsPatch = z.infer<typeof SafetySettingsPatchSchema>;
